<template>
  <div>
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          Search
        </h4>
      </div>
      <div class="card-body form-group">
        <div class="row">
          <div class="col-md-3">
            <label>Entity Type</label>
            <el-select
                class="select"
                size="large"
                placeholder="Search by Entity Type (project, job, task)"
                clearable
                data-vv-name="search"
                v-validate="'minFields'"
                v-model="query.entityType">
              <el-option
                  v-for="option in entityTypes"
                  class="select"
                  :value="option.value"
                  :label="option.label"
                  :key="option.label">
              </el-option>
            </el-select>
            <span class="text-danger" v-show="errors.has('search')">
              {{ searchFieldErrorText }}
            </span>
          </div>
          <div class="col-md-3">
            <label>Entity Id</label>
            <el-input
                type="search"
                data-vv-name="search"
                clearable
                placeholder="Entity Id"
                @keyup.enter.native="searchSubmit"
                v-validate="'minFields'"
                v-model="query.entityId"/>
            <span class="text-danger" v-show="errors.has('search')">
              {{ searchFieldErrorText }}
            </span>
          </div>
          <div class="col-md-3">
            <label>Log Level</label>
            <el-select
                id="levelCss"
                class="select"
                :popper-append-to-body="false"
                size="large"
                data-vv-name="search"
                v-validate="'minFields'"
                placeholder="Search by Log Level"
                clearable
                v-model="query.level">
              <el-option
                  v-for="option in logLevels"
                  class="select"
                  :value="option.value"
                  :label="option.label"
                  :key="option.label">
              </el-option>
            </el-select>
            <span class="text-danger" v-show="errors.has('search')">
              {{ searchFieldErrorText }}
            </span>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>Date Created</label>
              <el-date-picker
                  type="daterange"
                  align="right"
                  unlink-panels
                  range-separator="-"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  data-vv-name="search"
                  v-validate="'minFields'"
                  v-model="query.createdAt"
                  :format="constants.DATE.FORMAT.DISPLAY"
                  :value-format="constants.DATE.FORMAT.VALUE"
                  :picker-options="datePickerConfig.dateRange"
                  placeholder="Log Date">
              </el-date-picker>
              <span class="text-danger" v-show="errors.has('search')">
                {{ searchFieldErrorText }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <button type="button" @click="searchSubmit" class="btn btn-fill btn-info pull-right btn-search">
          Search
        </button>
        <button type="button" @click="clear" class="btn btn-fill btn-info pull-right btn-search">
          Clear
        </button>
      </div>
    </div>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-md-12">
            <el-table class="wrap-whole-words"
                      :data="tableData"
                      :max-height="tableHeight(400)"
                      style="width: 100%">
              <el-table-column
                min-width="75"
                prop="entityType"
                label="Entity Type">
              </el-table-column>
              <el-table-column
                min-width="75"
                prop="entityId"
                label="Entity Id">
                <template v-slot="props">
                  <template v-if="isLinkableLogEntity(props.row)">
                    <a href="#" @click.prevent="openEntity(props.row)">{{ props.row.entityId }}</a>
                  </template>
                  <template v-else>
                    {{ props.row.entityId }}
                  </template>
                </template>
              </el-table-column>
              <el-table-column
                min-width="75"
                prop="level"
                label="Log Level">
              </el-table-column>
              <el-table-column
                min-width="250"
                prop="message"
                label="Message">
              </el-table-column>
              <el-table-column
                min-width="75"
                label="Created At">
                <template v-slot="props">
                  {{ props.row.createdAt | formatDate }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 pagination-info">
            <span class="category">Showing {{total}} entries</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {DatePicker} from 'element-ui'
import {sync} from 'vuex-pathify'
import Constants from 'src/utilities/constants'
import {mapGetters} from 'vuex'
import {notifyError, capitalizeFirstLetter, getTableHeight} from 'src/utilities/utils'
import {OK} from 'http-status-codes'
import {minFields} from 'src/utilities/validation'
import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'

export default {
  props: ['data'],
  components: {
    DatePicker
  },
  computed: {
    query: sync('logs@query'),
    ...mapGetters({
      tableData: 'logs/results'
    }),
    total() {
      return this.tableData.length
    }
  },
  data() {
    return {
      constants: Constants,
      datePickerConfig,
      searchFieldErrorText: 'At least one search field is required',
      entityTypes: [
        {
          label: Constants.ENTITY_TYPES.PROJECT,
          value: Constants.ENTITY_TYPES.PROJECT
        },
        {
          label: Constants.ENTITY_TYPES.JOB,
          value: Constants.ENTITY_TYPES.JOB
        },
        {
          label: Constants.ENTITY_TYPES.TASK,
          value: Constants.ENTITY_TYPES.TASK
        },
        {
          label: Constants.ENTITY_TYPES.USER,
          value: Constants.ENTITY_TYPES.USER
        }
      ],
      logLevels: [
        {
          label: capitalizeFirstLetter(Constants.LOG_LEVELS.DEBUG),
          value: Constants.LOG_LEVELS.DEBUG
        },
        {
          label: capitalizeFirstLetter(Constants.LOG_LEVELS.INFO),
          value: Constants.LOG_LEVELS.INFO
        },
        {
          label: capitalizeFirstLetter(Constants.LOG_LEVELS.WARN),
          value: Constants.LOG_LEVELS.WARN
        },
        {
          label: capitalizeFirstLetter(Constants.LOG_LEVELS.ERROR),
          value: Constants.LOG_LEVELS.ERROR
        },
        {
          label: capitalizeFirstLetter(Constants.LOG_LEVELS.FATAL),
          value: Constants.LOG_LEVELS.FATAL
        }
      ]
    }
  },
  methods: {
    isLinkableLogEntity(log) {
      if (!log.entityId || log.entityId === 'null' || log.entityId === 'undefined') {
        return false
      }

      switch(log.entityType) {
        case Constants.ENTITIES.PROJECT:
          return !!log.entityId
        case Constants.ENTITIES.JOB:
          return !!(log.entityId && log.entityId !== 'ephemeral')
        case Constants.ENTITIES.TASK:
          return !!log.entityId
      }
    },
    openEntity(log) {
      switch(log.entityType) {
        case Constants.ENTITIES.PROJECT:
          this.$router.push({path: `/projects/${log.entityId}`})
          break
        case Constants.ENTITIES.JOB:
          this.$eventHub.$emit('show-modal-1', 'Edit Job', {id: log.entityId})
          break
        case Constants.ENTITIES.TASK:
          this.$eventHub.$emit('show-modal-2', 'Edit Task', {id: log.entityId})
      }
    },
    async searchSubmit() {
      if (await this.$validator.validateAll()) {
        const response = await this.$store.dispatch('logs/fetchResults')

        if (OK !== response.status) {
          notifyError(this, 'Unable to perform log search')
          return
        }

        if (response.data.length === 0) {
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
      }
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    clear() {
      this.$store.commit('logs/resetQuery')
      this.$store.commit('logs/resetResults')
    }
  },
  beforeCreate() {
    minFields('logs')
  },
  beforeMount() {
    this.$store.commit('logs/resetResults')
  }
}
</script>
